/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'./VIPRDecoratable',
	'./VIPRItemClass',
	'./VIPRFormatter'
], function(_, VIPRDecoratable, VIPRItemClass, VIPRFormatter) {

	// implements IContDataItem
	class VIPRContDataItem extends VIPRDecoratable {
		constructor(resultItem, context, content) {
			super(...arguments);

			this.context = context;
			this.queryResult = context && context.queryResult;
			this.content = content;

			// Ordinal slots normally does not take multiple dataitems (ie. stacked)
			// even for multi-measures case (ie. bar, column) the multiple ordinal dataitems are merged in to a single ordinal measure
			// and each measures are separated as series.
			// So at the end, from the VIPRData perspective a numeric dataitem (aka slot), has only one measure.
			this.formatter = new VIPRFormatter(this.context.index.dataitem, 0, this.context);
			//getTupleHeaders() return the array of itemClass, each Item Class contains an array of Tuple headers
			this.itemClass = new VIPRItemClass(resultItem.getDataItemList()[0], this.context);
			this.domain = this._createDomain(); // Min Max Data Item domain
		}

		/*
		 * Format data from min/max query
		 */
		_getMinMaxData(minmaxResult) {
			const resultItems = minmaxResult.getResultItemList();
			const data = [];
			for (let index = 0; index + 1 < resultItems.length; index += 2) {
				const resultDataItem = resultItems[index];
				const dataItem = resultDataItem.getDataItemList()[0];
				const min =  minmaxResult.getValue(0, index).value;
				const max = minmaxResult.getValue(0, index + 1).value;
				data.push({
					min: min,
					max: max,
					columnId: dataItem.getColumnId(),
					aggregation: dataItem.getAggregation(),
					explicit: this._explicitMinMax( min, max )
				});
			}
			return data;
		}

		_explicitMinMax(min, max){
			//TODO: Revisit the domain.explicit setting post R7
			//refer: EXPLORE-2277 EXPLORE-2692
			const visualization = this.content && this.content.getFeature('Visualization');
			return visualization && visualization.getType() === 'Tiledmap' && min !== undefined && min !==null && max !== undefined && max !== null;
		}

		/*
		 * Create a domain incorporating min/max from all measures
		 */
		_createMultiMeasureDomain(minmaxResult) {
			let overAllMin;
			let overAllMax;
			let overAllExplicit;
			const minmaxData = this._getMinMaxData(minmaxResult);
			minmaxData.forEach(({ min, max, explicit }) => {
				overAllMin = overAllMin !== undefined ? Math.min(overAllMin, min) : min;
				overAllMax = overAllMax !== undefined ? Math.max(overAllMax, max) : max;
				overAllExplicit = explicit;
			});

			return {
				min: overAllMin,
				max: overAllMax,
				explicit: overAllExplicit
			};
		}

		/*
		 * Create a domain only incorporating min/max for own data item
		 */
		_createNonMultiMeasureDomain(minmaxResult) {
			const minmaxData = this._getMinMaxData(minmaxResult);

			const columnId = this.itemClass.dataItem.getColumnId();
			const aggregation = this.itemClass.dataItem.getAggregation();

			const minmax = minmaxData.find(minmax => minmax.columnId === columnId && minmax.aggregation === aggregation);

			if(minmax) {
				return {
					min: minmax.min,
					max: minmax.max,
					explicit: minmax.explicit
				};
			} else {
				return null;
			}
		}

		/**
		 * @returns the domain (min/max) for the data item
		 */
		_createDomain() {
			let domain = null;

			const minmaxResult = this.context.minmaxResult;
			if (minmaxResult) {
				if(this.itemClass.getUniqueName() === '_multiMeasuresValue') {
					domain = this._createMultiMeasureDomain(minmaxResult);
				} else {
					domain = this._createNonMultiMeasureDomain(minmaxResult);
				}
			}
			return domain;
		}

		getUniqueName() {
			return this.itemClass.getUniqueName();
		}

		getCaption() {
			return this.itemClass.getCaption();
		}

		getType() {
			return 'cont';
		}

		getDomain( /*aggregation TODO for stacked charts*/ ) {
			return this.domain;
		}

		getFormatter() {
			return this.formatter;
		}

		getItemClass() {
			return this.itemClass;
		}
	}

	return VIPRContDataItem;
});
